import type { IChangeAction } from '@models/Forms/IForms';

export interface IChangeFieldBinding {
    key: string;
    tableKey?: string;
    rowId?: string;
    scopeId?: string;
}
export interface IChangeInvocation {
    activeTargets: Set<string>;
    scopeId?: string;
    isCurrent?: () => boolean;
}
export interface IChangeRequest {
    binding: IChangeFieldBinding;
    value: any;
    invocation: IChangeInvocation;
    rowData?: any;
    rowOldData?: any;
}
export type ChangeHandler = (request: IChangeRequest) => Promise<void>;

/** Поле документа, а не колонка строки (`includes('|Document')` ловил бы и `|DocumentType`). */
export const isDocumentFieldKey = (key: string | undefined): boolean => !!key?.startsWith('|Document|');

export const getFieldBindingKey = (binding: IChangeFieldBinding) =>
    JSON.stringify([binding.scopeId, binding.tableKey, binding.rowId, binding.key]);

export class ChangeRuntime {
    // Один ключ могут занимать несколько смонтированных контролов (одно поле в разных
    // местах формы): вызывается последний зарегистрированный, остальные ждут своей очереди.
    private readonly handlers = new Map<string, ChangeHandler[]>();
    readonly actions: ReadonlyMap<string, IChangeAction>;
    /** Отброшенные при сборке реестра changeActions: пустое имя или повтор. */
    readonly errors: readonly string[];

    constructor(actions: IChangeAction[] = []) {
        const registry = new Map<string, IChangeAction>();
        const errors: string[] = [];
        for (const action of actions) {
            if (!action.name) {
                errors.push('changeAction без имени пропущен.');
                continue;
            }
            if (registry.has(action.name)) {
                errors.push(`changeAction "${action.name}" объявлен повторно, используется первое объявление.`);
                continue;
            }
            registry.set(action.name, action);
        }
        this.actions = registry;
        this.errors = errors;
    }

    register(binding: IChangeFieldBinding, handler: ChangeHandler) {
        const key = getFieldBindingKey(binding);
        const stack = this.handlers.get(key);
        if (stack) stack.push(handler);
        else this.handlers.set(key, [handler]);
    }
    unregister(binding: IChangeFieldBinding, handler: ChangeHandler) {
        const key = getFieldBindingKey(binding);
        const stack = this.handlers.get(key);
        if (!stack) return;
        const index = stack.lastIndexOf(handler);
        if (index < 0) return;
        stack.splice(index, 1);
        if (stack.length === 0) this.handlers.delete(key);
    }

    private getHandler(key: string) {
        return this.handlers.get(key)?.at(-1);
    }

    hasHandler(binding: IChangeFieldBinding) {
        return (
            this.handlers.has(getFieldBindingKey(binding)) ||
            (binding.rowId !== undefined && this.handlers.has(getFieldBindingKey({ ...binding, rowId: undefined })))
        );
    }

    async invoke(
        binding: IChangeFieldBinding,
        value: any,
        invocation: IChangeInvocation,
        rowData?: any,
        rowOldData?: any,
    ) {
        if (invocation.isCurrent?.() === false) return false;
        const exact = getFieldBindingKey(binding);
        const handler =
            this.getHandler(exact) ??
            (binding.rowId !== undefined ? this.getHandler(getFieldBindingKey({ ...binding, rowId: undefined })) : undefined);
        if (!handler) return false;
        if (invocation.activeTargets.has(exact)) return false;
        invocation.activeTargets.add(exact);
        try {
            await handler({ binding, value, invocation, rowData, rowOldData });
            return true;
        } finally {
            invocation.activeTargets.delete(exact);
        }
    }
}
